import { ChangeEvent, useState } from 'react';
import { compact, uniq } from 'lodash';
import {
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { useSnackbar } from 'notistack';
import { Project } from 'lib/types/projects';

const MAX_URLS = 50;

interface Props {
  onNext: () => void;
  onBack: () => void;
  onClose: () => void;
  project?: Project;
}

export const ImportUrls = ({
  onNext, onBack, onClose, project,
}: Props) => {
  const API = useApi();
  const currentUser = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();

  const [errorMessage, setErrorMessage] = useState('');
  const [postUrls, setPostUrls] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const defaultTime = new Date().toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric',
  });
  const [importName, setImportName] = useState(project ? project.name : defaultTime);

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const urls = uniq(compact(e.target.value.split('\n')));
    if (urls.length > MAX_URLS) {
      setErrorMessage(`You may only import up to ${MAX_URLS} URLs at once.`);
    } else if (errorMessage) {
      setErrorMessage('');
    }
    setPostUrls(urls);
  };

  const performImport = async () => {
    if (errorMessage || !currentUser) return;

    setIsLoading(true);
    try {
      await API.importUrls(postUrls, importName, project?.id);
      onNext();
      enqueueSnackbar('We will let you know when your posts are done importing!', { variant: 'info' });
    } catch (err: any) {
      setErrorMessage(`There was an error importing your URLs: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentUser?.username) {
    return (
      <Dialog fullWidth onClose={onClose} aria-labelledby="title" open>
        Please set a username on your profile before importing URLs.
      </Dialog>
    );
  }

  return (
    <Dialog fullWidth onClose={onClose} aria-labelledby="title" open>
      <div className="absolute right-2 top-2">
        <IconButton aria-label="close" onClick={onClose}>
          <Close />
        </IconButton>
      </div>
      <DialogTitle id="title">Add URLs to Import</DialogTitle>
      <DialogContent>
        <div className="mb-2">
          Please paste a list of URLs to import into the box, one on each line.
          We support social media platforms including Twitter, Instagram,
          YouTube, and TikTok.
        </div>
        <TextField
          rows={12}
          onChange={onChange}
          multiline
          id="links"
          label="URLs"
          variant="outlined"
          fullWidth
        />
        <div className="text-red text-sm">
          {errorMessage}
        </div>
        <TextField
          onChange={(e) => setImportName(e.target.value)}
          margin="normal"
          id="importname"
          label="Import Name"
          value={importName}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <button type="button" className="btn-primary-outlined" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={performImport}
          disabled={!postUrls.length || isLoading || postUrls?.length > MAX_URLS}
        >
          {postUrls.length ? `Import ${postUrls.length} URLs` : 'Import'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
